import { createTheme, alpha } from '@mui/material/styles';

// Modern color palette
const primaryColor = {
  50: '#e3f2fd',
  100: '#bbdefb',
  200: '#90caf9',
  300: '#64b5f6',
  400: '#42a5f5',
  500: '#2196f3',
  600: '#1e88e5',
  700: '#1976d2',
  800: '#1565c0',
  900: '#0d47a1',
  main: '#3b82f6',
  light: '#60a5fa',
  dark: '#1d4ed8',
  contrastText: '#ffffff',
};

const secondaryColor = {
  main: '#8b5cf6',
  light: '#a78bfa',
  dark: '#6d28d9',
  contrastText: '#ffffff',
};

const successColor = {
  main: '#10b981',
  light: '#34d399',
  dark: '#059669',
  contrastText: '#ffffff',
};

const warningColor = {
  main: '#f59e0b',
  light: '#fbbf24',
  dark: '#d97706',
  contrastText: '#ffffff',
};

const errorColor = {
  main: '#ef4444',
  light: '#f87171',
  dark: '#dc2626',
  contrastText: '#ffffff',
};

const infoColor = {
  main: '#06b6d4',
  light: '#22d3ee',
  dark: '#0891b2',
  contrastText: '#ffffff',
};

// Custom shadows with modern feel
const customShadows = {
  card: '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)',
  cardHover: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
  dropdown: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
  button: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
  buttonHover: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
  dialog: '0 25px 50px -12px rgb(0 0 0 / 0.25)',
};

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: primaryColor,
    secondary: secondaryColor,
    success: successColor,
    warning: warningColor,
    error: errorColor,
    info: infoColor,
    background: {
      default: '#f8fafc',
      paper: '#ffffff',
      dark: '#0f172a',
    },
    text: {
      primary: '#1e293b',
      secondary: '#64748b',
      disabled: '#94a3b8',
    },
    divider: '#e2e8f0',
    grey: {
      50: '#f8fafc',
      100: '#f1f5f9',
      200: '#e2e8f0',
      300: '#cbd5e1',
      400: '#94a3b8',
      500: '#64748b',
      600: '#475569',
      700: '#334155',
      800: '#1e293b',
      900: '#0f172a',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontSize: '2.25rem',
      fontWeight: 700,
      lineHeight: 1.2,
      letterSpacing: '-0.02em',
    },
    h2: {
      fontSize: '1.875rem',
      fontWeight: 700,
      lineHeight: 1.3,
      letterSpacing: '-0.02em',
    },
    h3: {
      fontSize: '1.5rem',
      fontWeight: 600,
      lineHeight: 1.4,
      letterSpacing: '-0.01em',
    },
    h4: {
      fontSize: '1.25rem',
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h5: {
      fontSize: '1.125rem',
      fontWeight: 600,
      lineHeight: 1.5,
    },
    h6: {
      fontSize: '1rem',
      fontWeight: 600,
      lineHeight: 1.5,
    },
    subtitle1: {
      fontSize: '1rem',
      fontWeight: 500,
      lineHeight: 1.5,
    },
    subtitle2: {
      fontSize: '0.875rem',
      fontWeight: 500,
      lineHeight: 1.5,
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.6,
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.6,
    },
    caption: {
      fontSize: '0.75rem',
      lineHeight: 1.5,
    },
    button: {
      textTransform: 'none',
      fontWeight: 500,
    },
  },
  shape: {
    borderRadius: 12,
  },
  shadows: [
    'none',
    customShadows.card,
    customShadows.card,
    customShadows.cardHover,
    customShadows.cardHover,
    customShadows.dropdown,
    customShadows.dropdown,
    customShadows.dropdown,
    customShadows.dialog,
    customShadows.dialog,
    customShadows.dialog,
    customShadows.dialog,
    customShadows.dialog,
    customShadows.dialog,
    customShadows.dialog,
    customShadows.dialog,
    customShadows.dialog,
    customShadows.dialog,
    customShadows.dialog,
    customShadows.dialog,
    customShadows.dialog,
    customShadows.dialog,
    customShadows.dialog,
    customShadows.dialog,
    customShadows.dialog,
  ],
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          scrollbarWidth: 'thin',
          '&::-webkit-scrollbar': {
            width: '8px',
            height: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: '#f1f5f9',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#cbd5e1',
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: '#94a3b8',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          padding: '10px 20px',
          fontSize: '0.875rem',
          fontWeight: 500,
          boxShadow: 'none',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            boxShadow: customShadows.buttonHover,
            transform: 'translateY(-1px)',
          },
        },
        contained: {
          '&:hover': {
            boxShadow: customShadows.buttonHover,
          },
        },
        containedPrimary: {
          background: `linear-gradient(135deg, ${primaryColor.main} 0%, ${primaryColor.dark} 100%)`,
          '&:hover': {
            background: `linear-gradient(135deg, ${primaryColor.light} 0%, ${primaryColor.main} 100%)`,
          },
        },
        outlined: {
          borderWidth: '1.5px',
          '&:hover': {
            borderWidth: '1.5px',
            backgroundColor: alpha(primaryColor.main, 0.04),
          },
        },
        text: {
          '&:hover': {
            backgroundColor: alpha(primaryColor.main, 0.08),
          },
        },
        sizeSmall: {
          padding: '6px 14px',
          fontSize: '0.8125rem',
        },
        sizeLarge: {
          padding: '14px 28px',
          fontSize: '1rem',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: customShadows.card,
          border: '1px solid',
          borderColor: alpha('#e2e8f0', 0.8),
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            boxShadow: customShadows.cardHover,
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
        rounded: {
          borderRadius: 12,
        },
        elevation1: {
          boxShadow: customShadows.card,
        },
        elevation2: {
          boxShadow: customShadows.cardHover,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 10,
            transition: 'all 0.2s ease-in-out',
            '& fieldset': {
              borderColor: '#e2e8f0',
              borderWidth: '1.5px',
            },
            '&:hover fieldset': {
              borderColor: primaryColor.main,
            },
            '&.Mui-focused fieldset': {
              borderWidth: '2px',
            },
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          '& fieldset': {
            borderColor: '#e2e8f0',
            borderWidth: '1.5px',
          },
          '&:hover fieldset': {
            borderColor: primaryColor.main,
          },
          '&.Mui-focused fieldset': {
            borderWidth: '2px',
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          borderRadius: 10,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 500,
          fontSize: '0.75rem',
        },
        colorSuccess: {
          backgroundColor: alpha(successColor.main, 0.12),
          color: successColor.dark,
        },
        colorError: {
          backgroundColor: alpha(errorColor.main, 0.12),
          color: errorColor.dark,
        },
        colorWarning: {
          backgroundColor: alpha(warningColor.main, 0.12),
          color: warningColor.dark,
        },
        colorInfo: {
          backgroundColor: alpha(infoColor.main, 0.12),
          color: infoColor.dark,
        },
        colorPrimary: {
          backgroundColor: alpha(primaryColor.main, 0.12),
          color: primaryColor.dark,
        },
        colorSecondary: {
          backgroundColor: alpha(secondaryColor.main, 0.12),
          color: secondaryColor.dark,
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          alignItems: 'center',
        },
        standardSuccess: {
          backgroundColor: alpha(successColor.main, 0.12),
          color: successColor.dark,
          '& .MuiAlert-icon': {
            color: successColor.main,
          },
        },
        standardError: {
          backgroundColor: alpha(errorColor.main, 0.12),
          color: errorColor.dark,
          '& .MuiAlert-icon': {
            color: errorColor.main,
          },
        },
        standardWarning: {
          backgroundColor: alpha(warningColor.main, 0.12),
          color: warningColor.dark,
          '& .MuiAlert-icon': {
            color: warningColor.main,
          },
        },
        standardInfo: {
          backgroundColor: alpha(infoColor.main, 0.12),
          color: infoColor.dark,
          '& .MuiAlert-icon': {
            color: infoColor.main,
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 20,
          boxShadow: customShadows.dialog,
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontSize: '1.25rem',
          fontWeight: 600,
          padding: '24px 24px 16px',
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: '16px 24px',
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: '16px 24px 24px',
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-head': {
            backgroundColor: '#f8fafc',
            fontWeight: 600,
            color: '#475569',
            fontSize: '0.75rem',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            borderBottom: '2px solid #e2e8f0',
          },
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: alpha(primaryColor.main, 0.04),
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderColor: '#f1f5f9',
          padding: '16px',
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          marginBottom: 4,
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            backgroundColor: alpha(primaryColor.main, 0.08),
          },
          '&.Mui-selected': {
            backgroundColor: alpha(primaryColor.main, 0.12),
            '&:hover': {
              backgroundColor: alpha(primaryColor.main, 0.16),
            },
          },
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRight: '1px solid #e2e8f0',
          backgroundColor: '#ffffff',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#ffffff',
          color: '#1e293b',
          boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1)',
          borderBottom: '1px solid #e2e8f0',
        },
      },
    },
    MuiToolbar: {
      styleOverrides: {
        root: {
          minHeight: '72px !important',
        },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          backgroundColor: primaryColor.main,
          fontWeight: 600,
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: '#1e293b',
          fontSize: '0.75rem',
          padding: '8px 12px',
          borderRadius: 8,
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          backgroundColor: alpha(primaryColor.main, 0.12),
        },
        bar: {
          borderRadius: 4,
          background: `linear-gradient(90deg, ${primaryColor.main} 0%, ${primaryColor.light} 100%)`,
        },
      },
    },
    MuiStepper: {
      styleOverrides: {
        root: {
          '& .MuiStepIcon-root.Mui-active': {
            color: primaryColor.main,
          },
          '& .MuiStepIcon-root.Mui-completed': {
            color: successColor.main,
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            backgroundColor: alpha(primaryColor.main, 0.08),
          },
        },
      },
    },
    MuiBreadcrumbs: {
      styleOverrides: {
        root: {
          '& .MuiLink-root': {
            color: '#64748b',
            textDecoration: 'none',
            '&:hover': {
              color: primaryColor.main,
            },
          },
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        root: {
          width: 52,
          height: 28,
          padding: 0,
          '& .MuiSwitch-switchBase': {
            padding: 2,
            '&.Mui-checked': {
              transform: 'translateX(24px)',
              '& + .MuiSwitch-track': {
                backgroundColor: primaryColor.main,
                opacity: 1,
              },
            },
          },
          '& .MuiSwitch-thumb': {
            width: 24,
            height: 24,
            boxShadow: customShadows.button,
          },
          '& .MuiSwitch-track': {
            borderRadius: 14,
            backgroundColor: '#e2e8f0',
            opacity: 1,
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          '& .MuiTab-root': {
            textTransform: 'none',
            fontWeight: 500,
            fontSize: '0.875rem',
            minHeight: 48,
          },
        },
        indicator: {
          height: 3,
          borderRadius: '3px 3px 0 0',
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: 12,
          boxShadow: customShadows.dropdown,
          border: '1px solid #e2e8f0',
          marginTop: 8,
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          margin: '4px 8px',
          padding: '10px 16px',
          '&:hover': {
            backgroundColor: alpha(primaryColor.main, 0.08),
          },
        },
      },
    },
  },
});

export { customShadows };
export default theme;
